import { useState } from "react";
import { useCanWrite } from "@/app/auth";
import { deleteTarget } from "@/app/deleteTarget";
import { EntityDrawer } from "@/app/EntityDrawer";
import { EntityStatusPill } from "@/app/EntityStatusPill";
import { accounts, channels, customers, senderPolicies, type SenderPolicy, type SenderPolicyInput } from "@/app/master";
import { accountOptions, idOptions, loadingOf, nameOf, useList } from "@/app/queries";
import { Alert, Button, Dropdown, EmptyState, Input as TextInput, PageHeader, Table, TableWrap, Th, Toolbar, ToolbarCount } from "@/ui";
import { SenderPolicyForm } from "./SenderPolicyForm";
import { senderPolicyDraft, senderPolicyInput, senderRulesSummary } from "./senderPolicyDraft";

/** SID 替换（决策 D154）：按账号、MCC/MNC、通道条件替换发给上游的 sender。按优先级取第一条条件全部满足的策略，
 * 策略里先精确映射、再正则、最后默认替换；客户看到的 sender 与回执不变。新建与编辑在抽屉里，停用后不再生效。 */
export function SenderPolicies() {
  const canWrite = useCanWrite(); // 只读账号不显示写操作，服务端同样拒绝（docs/04 第 9 节）
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<{ row: SenderPolicy | null } | null>(null);
  const list = useList("sender-policies", senderPolicies);
  const accountList = useList("accounts", accounts).data;
  const channelList = useList("channels", channels).data;
  const accountOpts = accountOptions(accountList, useList("customers", customers).data);
  const scope = (p: SenderPolicy) => [
    p.account_ids.length ? `账号 ${p.account_ids.map((id) => nameOf(accountList, id)).join("、")}` : "",
    p.mcc ? `MCC-MNC ${p.mnc ? `${p.mcc}-${p.mnc}` : p.mcc}` : "",
    p.channel_ids.length ? `通道 ${p.channel_ids.map((id) => nameOf(channelList, id)).join("、")}` : "",
  ].filter(Boolean).join("；") || "全部流量";
  const keyword = q.trim().toLowerCase();
  const shown = (list.data ?? []).filter((p) => !keyword || `${p.name} ${scope(p)} ${p.mappings.map((m) => `${m.from} ${m.to}`).join(" ")}`.toLowerCase().includes(keyword));

  return (
    <>
      <PageHeader title="SID 替换" description="按条件替换发给上游的 sender：按优先级取第一条命中的策略，先精确映射、再正则、最后默认替换。客户的 sender、回执与路由不受影响。"
        actions={canWrite && <Button variant="primary" onClick={() => setEditing({ row: null })}>新建策略</Button>} />
      {list.error && <Alert tone="danger" title="加载失败">{list.error.message}</Alert>}
      <Toolbar right={<ToolbarCount>{shown.length} 条</ToolbarCount>}>
        <TextInput size="sm" className="ax-search" placeholder="搜索名称、条件或 sender" aria-label="搜索" value={q} onChange={(e) => setQ(e.target.value)} />
      </Toolbar>
      <TableWrap loading={loadingOf(list)}>
        {shown.length ? (
          <Table>
            <thead><tr><Th numeric w={80}>优先级</Th><Th w="name">名称</Th><Th w="wide">条件</Th><Th w="wide">替换规则</Th><Th w="status">状态</Th><Th w={200}>备注</Th><Th /></tr></thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id}>
                  <td className="ax-num">{p.priority}</td><td>{p.name}</td><td>{scope(p)}</td><td className="ax-mono">{senderRulesSummary(p)}</td>
                  <td><EntityStatusPill status={p.status} /></td><td className="ax-secondary">{p.note}</td>
                  <td>
                    <Dropdown iconOnly label="更多操作" variant="ghost" size="sm" align="end" items={[{ key: "edit", label: canWrite ? "编辑" : "查看", onSelect: () => setEditing({ row: p }) }]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !list.isPending && <EmptyState title="没有 SID 替换策略">没有策略时，发给上游的就是客户提交的 sender。</EmptyState>
        )}
      </TableWrap>
      {editing && (
        <EntityDrawer title={editing.row ? `编辑 SID 替换 ${editing.row.name}` : "新建 SID 替换策略"}
          initial={senderPolicyDraft(editing.row ?? undefined)} convert={senderPolicyInput}
          save={(body: SenderPolicyInput) => (editing.row ? senderPolicies.update(editing.row.id, body) : senderPolicies.create(body))}
          invalidate={["sender-policies"]} message={editing.row ? "已更新 SID 替换策略" : "已新建 SID 替换策略"} deletion={editing.row ? deleteTarget("SID 替换策略", editing.row.name, senderPolicies, editing.row.id, ["sender-policies"]) : undefined} onClose={() => setEditing(null)}>
          {(f) => <SenderPolicyForm draft={f.draft} errors={f.errors} set={f.set} isNew={!editing.row} accounts={accountOpts} channels={idOptions(channelList)} />}
        </EntityDrawer>
      )}
    </>
  );
}
